import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, Trash2, Copy } from "lucide-react";

/**
 * Mini-preview of a frame in the timeline — shows pitch + dots positions.
 */
function FrameThumbnail({ frame }) {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-12 rounded-md bg-emerald-700">
      <g stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" fill="none">
        <rect x="2" y="2" width="96" height="96" />
        <line x1="2" y1="50" x2="98" y2="50" />
        <circle cx="50" cy="50" r="9" />
      </g>
      {frame.players.map((p) => (
        <circle key={p.id} cx={p.x} cy={p.y} r="3" fill={p.color || "#0ea5e9"} stroke="white" strokeWidth="0.4" />
      ))}
      {frame.drawings.filter((d) => d.type === "arrow" || d.type === "line").map((d, i) => {
        const [a, b] = d.points || [];
        if (!a || !b) return null;
        return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={d.color} strokeWidth="0.6" />;
      })}
    </svg>
  );
}

/**
 * Timeline of frames — supports navigation, reorder, duration tweak and removal.
 */
export default function FrameTimeline({
  frames, frameIdx, setFrameIdx,
  onAddFrame, onRemoveFrame, onMoveFrame, onSetFrameName, onSetFrameDuration,
  disabled,
}) {
  return (
    <div className="mt-4" data-testid="timeline">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className="label-eyebrow">Timeline</span>
          <span className="text-xs text-slate-500">{frames.length} frame · transizione totale {frames.slice(0, -1).reduce((s, f) => s + (f.duration_ms || 2000), 0) / 1000}s</span>
        </div>
        <Button size="sm" variant="outline" onClick={onAddFrame} disabled={disabled} data-testid="add-frame-btn">
          <Copy size={13} className="mr-1" /> Duplica frame
        </Button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {frames.map((f, i) => (
          <div
            key={f.id}
            className={`min-w-[150px] rounded-xl border p-2 cursor-pointer transition flex-shrink-0 ${
              i === frameIdx ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 hover:bg-slate-50"
            }`}
            onClick={() => !disabled && setFrameIdx(i)}
            data-testid={`frame-${i}`}
          >
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[10px] font-bold text-slate-400">#{i + 1}</span>
              <div className="flex gap-0.5">
                <button
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  onClick={(e) => { e.stopPropagation(); onMoveFrame(i, -1); }}
                  disabled={i === 0 || disabled}
                  title="Sposta a sinistra"
                ><ChevronLeft size={12} /></button>
                <button
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  onClick={(e) => { e.stopPropagation(); onMoveFrame(i, 1); }}
                  disabled={i === frames.length - 1 || disabled}
                  title="Sposta a destra"
                ><ChevronRight size={12} /></button>
                <button
                  className="text-slate-400 hover:text-red-500 disabled:opacity-30"
                  onClick={(e) => { e.stopPropagation(); onRemoveFrame(i); }}
                  disabled={frames.length <= 1 || disabled}
                ><Trash2 size={12} /></button>
              </div>
            </div>
            <div className="mb-1.5">
              <FrameThumbnail frame={f} />
            </div>
            <Input
              value={f.name}
              onChange={(e) => onSetFrameName(i, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="h-7 text-xs"
            />
            <div className="mt-1 flex items-center gap-1">
              <Input
                type="number"
                min="200"
                step="100"
                value={f.duration_ms}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSetFrameDuration(i, e.target.value)}
                className="h-7 text-xs"
              />
              <span className="text-[10px] text-slate-500">ms</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
